import { useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { getTempSessionId } from '../lib/progressiveDataService';
import { getApiBaseUrl } from '../lib/getApiUrl';
import type { UserResource } from '@clerk/types';

// CB:BEGIN gate-contractor-route
// Production gating rules per PRODUCTION_FIX_INSTRUCTIONS.md and ENVIRONMENT_VARIABLES_EXACT.md
// In production: Only contractors can access contractor routes (strict enforcement)
// In dev/demo: Homeowners can access with demo flags
export function gateContractorRoute(userType: 'homeowner' | 'contractor' | 'unknown') {
  // Contractors always allowed
  if (userType === 'contractor') {
    return { allow: true, demo: false };
  }
  
  // Get runtime config to determine environment (hostname is source of truth)
  let appEnv: 'production' | 'development' | 'demo' = 'production';
  let demoMode = false;
  
  // Dynamic import is async, but we need sync result here. Import at top of module instead.
  // Fallback to env vars and hostname detection
  try {
    const host = (typeof window !== 'undefined' ? window.location.hostname : '').toLowerCase();
    if (host.endsWith('corebids.com') && (host === 'corebids.com' || host === 'www.corebids.com')) {
      appEnv = 'production';
      demoMode = false;
    } else if (host.startsWith('dev.') && host.endsWith('corebids.com')) {
      appEnv = 'development';
      demoMode = true;
    } else {
      appEnv = (import.meta.env.VITE_APP_ENV as any) || 'production';
      demoMode = import.meta.env.VITE_DEMO_MODE === 'true';
    }
  } catch {
    // Fallback to env vars if window not available
    appEnv = (import.meta.env.VITE_APP_ENV as any) || 'production';
    demoMode = import.meta.env.VITE_DEMO_MODE === 'true';
  }
  
  // PRODUCTION: Strict enforcement - only contractors allowed
  // Per ENVIRONMENT_VARIABLES_EXACT.md, production should have:
  // - VITE_DEMO_MODE=false
  // - VITE_ALLOW_CONTRACTOR_DEMO is NOT set (or false)
  // Route gating is now hostname-based, not env-var-based, so demo flags are ignored in production
  if (appEnv === 'production') {
    // Production: Block homeowners from contractor routes (no exceptions)
    console.log('🚦 gateContractorRoute: Production - blocking homeowner from contractor route');
    return { allow: false, demo: false };
  }
  
  // DEV/DEMO environments: Allow homeowners with demo flags or in dev mode
  if (appEnv === 'development' || appEnv === 'demo') {
    // In dev/demo, homeowners can access contractor routes for testing
    if (demoMode || import.meta.env.DEV) {
      console.log('🚦 gateContractorRoute: Dev/Demo - allowing homeowner with demo mode');
      return { allow: true, demo: true };
    }
  }
  
  // Localhost/unknown: Allow in dev mode
  if (import.meta.env.DEV) {
    return { allow: true, demo: true };
  }
  
  // Default: Block (safety first)
  return { allow: false, demo: false };
}
// CB:END gate-contractor-route

// CB:BEGIN ensure-homeowner-types
// ---- begin: ensureHomeowner resilience helpers ----
function sleep(ms: number) { return new Promise(res => setTimeout(res, ms)); }

async function fetchWithTimeout(input: RequestInfo, init: RequestInit & { timeoutMs?: number } = {}) {
  const { timeoutMs = 6000, ...rest } = init as any;
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try { return await fetch(input as any, { ...rest, signal: controller.signal } as any); }
  finally { clearTimeout(id); }
}

async function fetchRetry(input: RequestInfo, init: RequestInit & { timeoutMs?: number } = {}, attempts = 2) {
  let lastErr: any = null;
  for (let i = 0; i < attempts; i++) {
    try {
      const r = await fetchWithTimeout(input, init);
      // Retry on transient upstream issues
      if (r && [502,503,504].includes(r.status)) {
        lastErr = new Error(`transient ${r.status}`);
      } else {
        return r;
      }
    } catch (e) {
      lastErr = e;
    }
    await sleep(300 * (i + 1));
  }
  throw lastErr || new Error('network');
}
// ---- end: ensureHomeowner resilience helpers ----

export type EnsureHomeownerResult =
  | { ok: true; homeownerId: string }
  | { ok: false; reason: 'not-signed-in' | 'network' | 'server' | 'not-found'; detail?: string };

// CB:BEGIN ensure-homeowner-fix
export async function ensureHomeowner(user: UserResource | null, _address?: string): Promise<EnsureHomeownerResult> {
  // Guard: Clerk user may be momentarily undefined even when isSignedIn is true
  if (!user || !(user as any)?.id) return { ok: false, reason: 'not-signed-in' };

  const clerkId = user.id;
  const email = user.primaryEmailAddress?.emailAddress || user.emailAddresses?.[0]?.emailAddress || '';
  const firstName = user.firstName || '';
  const lastName = user.lastName || '';

  const base = getApiBaseUrl();

  // 1) Try GET (fast path) — if 404, we'll POST to create idempotently, then GET again
  const getOnce = async () => {
    const r = await fetchRetry(`${base}/api/homeowner/${encodeURIComponent(clerkId)}`, { 
      method: 'GET',
      credentials: 'include',
      timeoutMs: 6000 
    });
    if (r.status === 404) return null;
    if (!r.ok) throw new Error(`server homeowner fetch failed: ${r.status}`);
    return r.json();
  };

  try {
    const found = await getOnce();
    if (found?.homeowner?.id) return { ok: true, homeownerId: found.homeowner.id };
  } catch (e) {
    // Downgrade noisy pre-fetch errors; will retry below
    console.debug('ensure-homeowner GET pre-check skipped:', (e as Error).message);
  }

  // 2) POST ensure-homeowner (idempotent on server)
  try {
    const r = await fetchRetry(`${base}/api/users/ensure-homeowner`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clerkId,
        email,
        firstName,
        lastName,
        // CB:BEGIN merge-temp-on-ensure
        tempSessionId: typeof getTempSessionId === 'function' ? getTempSessionId() : (localStorage.getItem('temp_session_id') || undefined),
        // CB:END merge-temp-on-ensure
      }),
      timeoutMs: 7000 
    });

    if (!r.ok) {
      const txt = await r.text().catch(() => '');
      throw new Error(`ensure-homeowner POST failed: ${r.status} ${txt}`);
    }

    const data = await r.json();
    if (data?.homeowner?.id && data?.user) return { ok: true, homeownerId: data.homeowner.id };
  } catch (e) {
    console.warn('ensure-homeowner failed:', e);
    // fall through to final GET attempt
  }

  // 3) GET again after POST attempt
  try {
    const found = await getOnce();
    if (found?.homeowner?.id) return { ok: true, homeownerId: found.homeowner.id };
  } catch (e) {
    console.warn('ensure-homeowner GET failed (post):', e);
  }

  console.info('ensure-homeowner: server unavailable, continuing in demo mode');
  return { ok: false, reason: 'server', detail: 'Failed to ensure homeowner' };
}
// CB:END ensure-homeowner-fix
// CB:END ensure-homeowner-types

export function useEnsureHomeowner() {
  const { isSignedIn, user } = useUser();
  const didRun = useRef(false);

  useEffect(() => {
    if (!isSignedIn || !user || didRun.current) return;

    // CB:BEGIN skip-for-contractors
    // If user is a contractor, do NOT create homeowner records
    try {
      const meta = (user.publicMetadata as any) || {};
      const usertype = (meta.usertype || meta.userType || '').toString().toLowerCase();
      if (usertype === 'contractor') return;
    } catch {}

    // Skip if current URL is a contractor screen (defensive, in case metadata isn't set yet)
    try {
      const path = window.location.pathname || '';
      if (/^\/(contractor|current-bids|project-details|contractor-dashboard|contractor-verification)/i.test(path)) return;
    } catch {}
    // CB:END skip-for-contractors
    didRun.current = true;
    
    (async () => {
      try {
        const clerkAddress = (user.publicMetadata as any)?.address as string | undefined;
        const fallbackAddress = clerkAddress || '';
        
        const result = await ensureHomeowner(user, fallbackAddress);
        if (result.ok) {
          console.log('✅ Homeowner record ensured:', result.homeownerId);
        } else {
          console.warn('ensure-homeowner failed:', result.reason, result.detail);
        }
      } catch (err) {
        console.warn('ensure-homeowner failed:', err);
      }
    })();
  }, [isSignedIn, user]);
}
